import { LogOut, type LucideIcon } from 'lucide-react';

export interface SidebarNavItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
}

interface AppSidebarProps {
  navItems: SidebarNavItem[];
  activePath: string;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
  onNavigate: (path: string) => void;
  onSignOut: () => void;
}

const isNavItemActive = (itemPath: string, activePath: string): boolean => {
  if (itemPath === '/') {
    return activePath === '/';
  }

  return activePath === itemPath || activePath.startsWith(`${itemPath}/`);
};

export default function AppSidebar({
  navItems,
  activePath,
  isMobileOpen,
  onCloseMobile,
  onNavigate,
  onSignOut,
}: AppSidebarProps) {
  const sidebarContent = (
    <>
      <div className="p-6 flex items-center gap-3">
        <div className="w-8 h-8 bg-sidebar-accent rounded flex items-center justify-center text-primary font-bold text-2xl">
          D
        </div>
        <div className="font-bold text-lg tracking-tight">DI HR</div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = isNavItemActive(item.path, activePath);

          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-all cursor-pointer ${
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-sm'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
              }`}
            >
              <item.icon
                size={18}
                className={
                  isActive ? 'text-sidebar-accent-foreground' : 'text-sidebar-foreground/50'
                }
              />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-sidebar-border/30">
        <button
          type="button"
          onClick={onSignOut}
          className="w-full flex items-center gap-3 px-3 py-2 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 rounded-lg transition-colors text-base font-medium cursor-pointer"
        >
          <LogOut size={18} className="text-sidebar-foreground/50" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <>
      <aside className="hidden lg:flex w-64 bg-sidebar text-sidebar-foreground flex-col shrink-0 z-40">
        {sidebarContent}
      </aside>

      <div
        className={`fixed inset-0 z-50 lg:hidden transition ${
          isMobileOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onCloseMobile}
          className={`absolute inset-0 bg-black/35 transition-opacity ${
            isMobileOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <aside
          className={`absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-sidebar text-sidebar-foreground shadow-2xl transition-transform ${
            isMobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {sidebarContent}
        </aside>
      </div>
    </>
  );
}
